/**
 * Moves a VM from one customer to another, on one screen.
 *
 * Left card: the customer giving a machine up, and the machine. Right card: the customer taking it
 * over, the user who becomes its owner of record, and Apply — which lights up as soon as both sides
 * are chosen. There are no steps and no confirmation page; the outcome is reported in the right
 * card, and the form is ready for the next move.
 *
 * Three routes open this page and they only pre-fill it: `/tenants/move-vm` starts empty,
 * `/tenants/:tenantId/transfer-ownership` pre-selects the customer, and
 * `/tenants/:tenantId/resources/:resourceId/transfer-ownership` the machine as well.
 *
 * The rules — what moves, what stays with the customer who paid, what refuses a move — live in the
 * operator guide behind "How this works", not on the screen.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Copy,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

import { HowThisWorks } from "@/components/billing-cycles";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { PageHeader } from "@/components/common/page-header";
import { TenantCombobox } from "@/components/common/TenantCombobox";
import { TransferMachinePicker } from "@/components/resources";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { resourceKeys } from "@/hooks/useResourceQueries";
import { tenantKeys, useTenantDetail, useTenantMembers } from "@/hooks/useTenantQueries";
import { awaitRefresh } from "@/lib/awaitRefresh";
import { formatDateTime } from "@/lib/utils";
import { adminResourceService } from "@/services/admin-resource.service";
import {
  TRANSFER_REASON_MAX_LENGTH,
  TRANSFER_REASON_MIN_LENGTH,
  type ResetVmPasswordResult,
  type Resource,
  type TransferResourceOwnershipResult,
} from "@/types/admin";

/**
 * The reason recorded on both customers' audit rows.
 *
 * The backend requires one (5–500 characters), but asking the operator for it before Apply would put
 * a third required field on a two-choice screen. So the note is optional and a factual sentence
 * naming both customers stands in for it; a note too short to stand alone is kept, after that
 * sentence, rather than dropped.
 */
function transferReason(note: string, from: string, to: string): string {
  const fallback = `Moved from ${from} to ${to} in the admin console`;
  const trimmed = note.trim();
  const reason = !trimmed
    ? fallback
    : trimmed.length >= TRANSFER_REASON_MIN_LENGTH
      ? trimmed
      : `${fallback}: ${trimmed}`;

  return reason.slice(0, TRANSFER_REASON_MAX_LENGTH);
}

function copyToClipboard(value: string) {
  navigator.clipboard.writeText(value);
  toast.success("Copied");
}

function CopyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <Input value={value} readOnly aria-label={label} className="font-mono" />
      <Button
        variant="outline"
        size="icon"
        aria-label={`Copy the ${label.toLowerCase()}`}
        className="h-9 w-9 shrink-0"
        onClick={() => copyToClipboard(value)}
      >
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * What the move did, in the words an operator repeats to the customer — and only the lines that
 * apply to this move, so a clean move reads as two sentences.
 */
function MoveResult({
  result,
  reset,
  isResetting,
  disabled,
  onReset,
}: {
  result: TransferResourceOwnershipResult;
  reset: ResetVmPasswordResult | null;
  isResetting: boolean;
  /** True while a transfer is in flight elsewhere on the page — the overlay already blocks the
   *  mouse, but the button itself must refuse a keyboard Enter too. */
  disabled: boolean;
  onReset: () => void;
}) {
  return (
    <div
      role="status"
      className="space-y-3 rounded-lg border border-hairline bg-panel-2 p-4"
    >
      <p className="flex items-start gap-2 font-medium">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        {result.resourceName} now belongs to {result.newTenantName}
      </p>

      <ul className="space-y-1 text-sm text-muted-foreground">
        <li>
          Moved from {result.previousTenantName}. Owner of record:{" "}
          {result.newUserEmail}.
        </li>
        <li>
          {result.inheritedUntil
            ? `Paid through ${formatDateTime(result.inheritedUntil)}, carried over at no charge.`
            : `No paid time was left — ${result.newTenantName} renews it.`}
        </li>
        {result.cancelledRenewalOrderNumber && (
          <li>
            {result.previousTenantName}'s unpaid renewal order{" "}
            <span className="font-mono text-xs">
              {result.cancelledRenewalOrderNumber}
            </span>{" "}
            was cancelled.
          </li>
        )}
        {result.renewalOrderLeftForRefund && (
          <li className="text-warn-ink">
            Renewal order{" "}
            <span className="font-mono text-xs">
              {result.renewalOrderLeftForRefundNumber}
            </span>{" "}
            already holds a payment and was left standing — decide on a refund
            for {result.previousTenantName}.
          </li>
        )}
      </ul>

      {reset ? (
        <div className="space-y-2 border-t border-hairline pt-3">
          <p className="text-sm font-medium">
            New password — shown only once
          </p>
          <CopyField label="Username" value={reset.username} />
          <CopyField label="New password" value={reset.newPassword} />
          {reset.requiresReboot && (
            <p className="text-sm text-warn-ink">
              It takes effect after the machine restarts.
            </p>
          )}
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          disabled={isResetting || disabled}
          onClick={onReset}
        >
          {isResetting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RotateCcw className="h-4 w-4" />
          )}
          Reset VM password
        </Button>
      )}
    </div>
  );
}

export function TransferOwnershipPage() {
  const { tenantId, resourceId } = useParams<{
    tenantId?: string;
    resourceId?: string;
  }>();
  const queryClient = useQueryClient();

  const [sourceTenantId, setSourceTenantId] = useState(tenantId ?? "");
  const [machine, setMachine] = useState<Resource | null>(null);
  // The machine named in the URL is pre-selected once. After the operator picks anything, or a move
  // lands, the URL no longer describes what is on screen.
  const [preselectId, setPreselectId] = useState(resourceId);
  const [targetTenantId, setTargetTenantId] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const [note, setNote] = useState("");
  const [result, setResult] = useState<TransferResourceOwnershipResult | null>(
    null,
  );
  // Component state, not query cache: the password comes back once and can never be fetched again.
  const [reset, setReset] = useState<ResetVmPasswordResult | null>(null);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  const {
    data: sourceTenant,
    isError: sourceTenantFailed,
    isFetching: isFetchingSourceTenant,
    refetch: refetchSourceTenant,
  } = useTenantDetail(sourceTenantId);
  const {
    data: targetTenant,
    isError: targetTenantFailed,
    isFetching: isFetchingTargetTenant,
    refetch: refetchTargetTenant,
  } = useTenantDetail(targetTenantId);
  const {
    data: members,
    isFetching: isLoadingMembers,
    isError: membersFailed,
    refetch: refetchMembers,
  } = useTenantMembers(targetTenantId);

  // Only an ACTIVE member can own a resource: the backend checks membership, and anyone else would be
  // refused after Apply.
  const activeMembers = useMemo(
    () => (members ?? []).filter((member) => member.status === "Active"),
    [members],
  );

  // The owner is the obvious default, so the common case needs no choice here at all.
  useEffect(() => {
    if (activeMembers.length === 0) return;
    setTargetUserId((current) => {
      if (current && activeMembers.some((m) => m.userId === current)) {
        return current;
      }
      const owner = activeMembers.find((m) => m.role === "Owner");
      return (owner ?? activeMembers[0]).userId;
    });
  }, [activeMembers]);

  const sourceName = sourceTenant?.name ?? "the current owner";
  const targetName = targetTenant?.name ?? "the receiving customer";

  const chooseSource = (id: string) => {
    setSourceTenantId(id);
    setMachine(null);
    setPreselectId(undefined);
    if (id === targetTenantId) {
      setTargetTenantId("");
      setTargetUserId("");
    }
  };

  // Stable: the picker calls it from an effect when it pre-selects the machine named in the URL.
  const chooseMachine = useCallback((row: Resource) => {
    setMachine(row);
    setPreselectId(undefined);
    setResult(null);
    setReset(null);
  }, []);

  const chooseTarget = (id: string) => {
    setTargetTenantId(id);
    setTargetUserId("");
  };

  const transferMutation = useMutation({
    mutationFn: (variables: {
      resourceId: string;
      // Not sent to the API — the endpoint takes the machine's tenant from the resource itself —
      // but carried on the mutation's own variables so `onSettled` below can invalidate the
      // SOURCE tenant's cached detail/members without depending on component state that may have
      // already moved on by the time the request settles.
      sourceTenantId: string;
      targetTenantId: string;
      targetUserId: string;
      reason: string;
    }) =>
      adminResourceService.transferResourceOwnership(variables.resourceId, {
        targetTenantId: variables.targetTenantId,
        targetUserId: variables.targetUserId,
        reason: variables.reason,
      }),
    onSuccess: (data) => {
      setResult(data);
      setReset(null);
      setMachine(null);
      setPreselectId(undefined);
      setTargetTenantId("");
      setTargetUserId("");
      setNote("");
      toast.success(`${data.resourceName} now belongs to ${data.newTenantName}.`);
    },
    onError: (error: Error) => {
      toast.error(error.message || "The VM could not be moved.");
    },
    // Returned, and moved from onSuccess: the mutation must stay `isPending` — and the page stay
    // locked behind BusyOverlay — until both lists have actually refetched. Left as `onSuccess`
    // fire-and-forget, the moved VM could still show as movable in the old tenant's picker (and be
    // applied again) for as long as the invalidated query takes to answer.
    // `awaitRefresh` caps that wait at 8s: a stalled re-read must not hang the page-blocking
    // overlay indefinitely. The refetch itself keeps running past the cap and the table/tenant
    // counts catch up whenever it lands.
    //
    // `tenantKeys.detail(id)` — not a bare `["tenants"]`, which matches nothing real (`tenantKeys.all`
    // is `['admin', 'tenants']`) and so refetched neither tenant's cached detail nor its members
    // (`tenantKeys.members` nests under `tenantKeys.detail`). Both sides of the move need it: the
    // source tenant's resource/member counts and the target's, which the "To" card is showing right
    // now.
    onSettled: (_data, _error, variables) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({ queryKey: resourceKeys.all }),
          queryClient.invalidateQueries({
            queryKey: tenantKeys.detail(variables.sourceTenantId),
          }),
          queryClient.invalidateQueries({
            queryKey: tenantKeys.detail(variables.targetTenantId),
          }),
          queryClient.invalidateQueries({ queryKey: tenantKeys.lists() }),
        ]),
      ),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (id: string) => adminResourceService.resetVmPassword(id),
    // `id` here is the mutation's own variable — the resource id the reset was asked for — not
    // `result?.resourceId` read fresh. If a second transfer's `onSuccess` has already replaced
    // `result` with a different VM by the time this answers, showing the new password under the
    // wrong VM's name would be worse than showing nothing, so the reset is only accepted when it
    // still matches what's on screen.
    onSuccess: (data, id) => {
      setConfirmResetOpen(false);
      if (result?.resourceId !== id) return;
      setReset(data);
      toast.success("Password reset.");
    },
    onError: (error: Error) => {
      setConfirmResetOpen(false);
      toast.error(error.message || "Failed to reset the VM password.");
    },
  });

  const targetUserIsMember = activeMembers.some(
    (member) => member.userId === targetUserId,
  );
  // Locked while either mutation is in flight: a pending reset names a specific VM in the result
  // panel, and applying a new transfer (or picking a different machine) while it's out is exactly
  // the race that can hand that VM's password to a result that no longer names it.
  const anyMutationPending =
    transferMutation.isPending || resetPasswordMutation.isPending;
  const canApply =
    !!machine &&
    !!targetTenantId &&
    !isLoadingMembers &&
    targetUserIsMember &&
    !anyMutationPending;

  const hint = !machine
    ? "Pick a machine on the left."
    : !targetTenantId
      ? "Pick the customer taking it over."
      : null;

  const apply = () => {
    if (!machine || !canApply) return;
    transferMutation.mutate({
      resourceId: machine.id,
      sourceTenantId,
      targetTenantId,
      targetUserId,
      reason: transferReason(note, sourceName, targetName),
    });
  };

  return (
    <div className="w-full space-y-4 py-4">
      <PageHeader
        title="Move VM"
        subtitle="Pick the machine on the left and the customer taking it over on the right. The machine itself is not touched — only who owns and pays for it changes."
        action={<HowThisWorks guide="vm-ownership-transfer" anchor="what-moves" />}
      />

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card className="shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">From</CardTitle>
            <CardDescription>
              The customer giving a machine up, and the machine.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="move-source-tenant">Current owner</Label>
              <TenantCombobox
                id="move-source-tenant"
                value={sourceTenantId}
                selectedLabel={sourceTenant?.name}
                onChange={chooseSource}
                disabled={anyMutationPending}
              />
              {/* Only when there is truly nothing to show: a failed BACKGROUND refetch keeps the
                  last-known name on the combobox (`selectedLabel`), and reporting an error under a
                  field that still reads correctly would contradict what the operator sees above it. */}
              {sourceTenantFailed && !sourceTenant && (
                <p className="flex items-center gap-2 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  This customer's details could not be loaded.
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-sm text-destructive underline"
                    disabled={isFetchingSourceTenant}
                    onClick={() => refetchSourceTenant()}
                  >
                    {isFetchingSourceTenant && <Spinner className="mr-1" />}
                    Retry
                  </Button>
                </p>
              )}
            </div>

            {sourceTenantId ? (
              <TransferMachinePicker
                tenantId={sourceTenantId}
                tenantName={sourceTenant?.name}
                selectedId={machine?.id ?? null}
                preselectId={preselectId}
                onSelect={chooseMachine}
                disabled={anyMutationPending}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Pick a customer to list their machines.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">To</CardTitle>
            <CardDescription>The customer taking the machine over.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {result && (
              <MoveResult
                result={result}
                reset={reset}
                isResetting={resetPasswordMutation.isPending}
                disabled={transferMutation.isPending}
                onReset={() => setConfirmResetOpen(true)}
              />
            )}

            <div className="space-y-2">
              <Label htmlFor="move-target-tenant">New owner</Label>
              <TenantCombobox
                id="move-target-tenant"
                value={targetTenantId}
                selectedLabel={targetTenant?.name}
                excludeIds={sourceTenantId ? [sourceTenantId] : undefined}
                onChange={chooseTarget}
                disabled={anyMutationPending}
              />
              {/* Same reasoning as the source tenant's error line above: only when there is no
                  name to show at all — a failed background refetch keeps the last-known one on the
                  combobox. */}
              {targetTenantFailed && !targetTenant && (
                <p className="flex items-center gap-2 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  This customer's details could not be loaded.
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-sm text-destructive underline"
                    disabled={isFetchingTargetTenant}
                    onClick={() => refetchTargetTenant()}
                  >
                    {isFetchingTargetTenant && <Spinner className="mr-1" />}
                    Retry
                  </Button>
                </p>
              )}
            </div>

            {targetTenantId && (
              <div className="space-y-2">
                <Label htmlFor="move-target-user">Owner of record</Label>
                {isLoadingMembers ? (
                  <p className="text-sm text-muted-foreground">
                    Loading members…
                  </p>
                ) : membersFailed ? (
                  <p className="flex items-start gap-2 text-sm text-destructive">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      This customer's members could not be loaded.{" "}
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-sm text-destructive underline"
                        onClick={() => refetchMembers()}
                      >
                        Retry
                      </Button>
                    </span>
                  </p>
                ) : activeMembers.length === 0 ? (
                  <p className="flex items-start gap-2 text-sm text-destructive">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    This customer has no active member, so nobody there could see
                    the machine. Activate a member first.
                  </p>
                ) : (
                  <Select
                    value={targetUserId}
                    onValueChange={setTargetUserId}
                    disabled={anyMutationPending}
                  >
                    <SelectTrigger id="move-target-user" className="h-9 w-full">
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeMembers.map((member) => (
                        <SelectItem key={member.userId} value={member.userId}>
                          {member.email}
                          {member.role === "Owner" ? " · Owner" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="move-note">
                Note{" "}
                <span className="font-normal text-muted-foreground">
                  (optional)
                </span>
              </Label>
              <Textarea
                id="move-note"
                rows={2}
                maxLength={TRANSFER_REASON_MAX_LENGTH}
                placeholder="e.g. Sold to another customer — ticket #1234"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                disabled={anyMutationPending}
              />
            </div>

            <div className="space-y-3 border-t border-hairline pt-4">
              {machine && targetTenantId && (
                <div className="text-sm">
                  <p className="flex flex-wrap items-center gap-x-1.5">
                    <span className="font-medium">{machine.name}</span>
                    <span className="text-muted-foreground">from</span>
                    <span>{sourceName}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-medium">{targetName}</span>
                  </p>
                  <p className="text-muted-foreground">
                    {machine.expiresAt
                      ? `Paid through ${formatDateTime(machine.expiresAt)} goes with it, at no charge.`
                      : "No paid time left — the new owner renews it."}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <Button disabled={!canApply} onClick={apply}>
                  {transferMutation.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  Apply
                </Button>
                {hint && (
                  <p className="text-sm text-muted-foreground">{hint}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmResetOpen}
        onOpenChange={setConfirmResetOpen}
        title="Reset the VM password?"
        description={`This replaces the password for ${result?.resourceName ?? "this machine"}. It takes effect only after the machine restarts.`}
        confirmText="Reset password"
        onConfirm={() => result && resetPasswordMutation.mutate(result.resourceId)}
        isPending={resetPasswordMutation.isPending}
      />

      {/* Only one of the two mutations can ever be pending at once — canApply and the machine
          picker are locked while a reset is out, and the reset button sits behind this same
          overlay while a transfer is out — so one label always describes the one write in
          flight. */}
      <BusyOverlay
        open={anyMutationPending}
        label={
          transferMutation.isPending
            ? "Moving the machine…"
            : "Resetting the VM password…"
        }
      />
    </div>
  );
}
